import classnames from 'classnames';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import React from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';

import { CalendarConfig, Notification, ListConfig, Language } from '../types';
import CalendarDateButton from './CalendarDateButton';
import CalendarNotification from './CalendarNotification';
import CalendarViewHeader from './CalendarViewHeader';

export interface CalendarViewProps {
    timeZoneDifferent: boolean;
    calendar?: CalendarConfig;
    onNavigate: (v: ZonedDate) => void;
    onSelect: (v: ZonedDate) => void;
    displayType?: string;
    notification?: Notification;
    selected?: ZonedDate;
    language?: Language;
    listConfig?: ListConfig;
    bankHolidayDates?: ZonedDate[];
}

const CalendarView = ({
    timeZoneDifferent,
    calendar,
    onNavigate,
    onSelect,
    displayType,
    notification,
    selected,
    language,
    listConfig,
    bankHolidayDates,
}: CalendarViewProps): JSX.Element | null => {
    const isBankHoliday = (day) => bankHolidayDates?.some((item) => isSameDay(getZonedDate(item), getZonedDate(day)));
    const today = getZonedDate();
    if (!calendar) return null;
    return (
        <div className={classnames('sgw-calendar__view-wrapper', displayType)}>
            <CalendarViewHeader calendar={calendar} onNavigate={onNavigate} />

            <ul>
                {(listConfig?.reverse ? calendar.list.reverse() : calendar.list).map((row) => {
                    return (listConfig?.reverse ? row.list.reverse() : row.list).map((data) => {
                        return data.outOfMonth || (data.disabled && !listConfig?.showDisabledDate) ? null : (
                            <li key={data.id}>
                                <CalendarDateButton data={data} onSelect={onSelect} box />
                                {data.selected && (
                                    <CalendarNotification
                                        notification={notification}
                                        selected={data.zonedDate}
                                        timeZoneDifferent={timeZoneDifferent}
                                        language={language}
                                    />
                                )}
                            </li>
                        );
                    });
                })}
            </ul>

            <table role="grid">
                <caption>{calendar.title.aria}</caption>
                <thead>
                    <tr>
                        {calendar.tableHead.map((data) => (
                            <th key={data.id} className="name" scope="col">
                                <abbr title={data.aria}>{data.text}</abbr>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {calendar.list.map((row) => (
                        <tr key={row.id}>
                            {row.list.map((data) => {
                                const isBankHolidayAndNotInPast =
                                    isBankHoliday(data.zonedDate) && isBefore(today, getZonedDate(data.zonedDate));
                                return (
                                    <td
                                        key={data.id}
                                        className={classnames('sgw-calendar__date-field', {
                                            'out-of-month': data.outOfMonth,
                                        })}
                                        aria-label={`${data.detail.dateText}.${data.detail.monthText}`}
                                    >
                                        <CalendarDateButton
                                            data={data}
                                            onSelect={onSelect}
                                            bankHoliday={isBankHolidayAndNotInPast}
                                        />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>

            {(!!selected || timeZoneDifferent) && (
                <CalendarNotification
                    notification={notification}
                    selected={selected}
                    timeZoneDifferent={timeZoneDifferent}
                    language={language}
                />
            )}
        </div>
    );
};

export default CalendarView;
